import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline"
import { rankItem } from "@tanstack/match-sorter-utils"
import {
  FilterFn,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FC, useState } from "react"
import { Subcategory, Test } from "../../types/LabReport"
import { classNames } from "../../utils/classNames"
import { DebouncedInput } from "../common/DebouncedInput"
import Modal from "../common/Modal"
import { BarGauge } from "../visualizations/BarGauge"
import {
  flagToTitle,
  formatNotes,
  getFlagStyles,
  getResultValueColor,
  shouldDisplayGauge,
  strToNum,
} from "./utils"

interface ResultsChartsProps {
  results: Subcategory
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value)

  // Store the itemRank info
  addMeta({
    itemRank,
  })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

const columnHelper = createColumnHelper<Test>()
const columns = [
  columnHelper.accessor("name", {
    header: "Test",
    size: 1,
  }),
  columnHelper.accessor("result", {
    header: "Result",
    size: 1,
    cell: (info) => {
      const row = info.row.original
      return (
        <div className={classNames("max-w-[60vw] break-words", getResultValueColor(row))}>
          {info.getValue()}
        </div>
      )
    },
  }),
  columnHelper.accessor("range", {
    header: "Range",
    cell: (info) => {
      const row = info.row.original
      if (row.range) {
        return (
          <div className="mr-4">
            {row.range[0]} - {row.range[1]}
          </div>
        )
      }
      if (!row.min && !row.max) return null
      return (
        <div className="mr-4">
          {row.min} - {row.max}
        </div>
      )
    },
    size: 1,
  }),
  columnHelper.accessor("unit", {
    header: "Unit",
    size: 1,
  }),
  columnHelper.display({
    id: "barGauge",
    size: 10,
    cell: (info) => {
      const row = info.row.original

      return (
        <div className="mr-4">
          {shouldDisplayGauge(row) ? (
            <BarGauge value={strToNum(row.result)} min={row.min} max={row.max} range={row.range} />
          ) : null}
        </div>
      )
    },
  }),
  columnHelper.accessor("abnormal", {
    header: "Flag",
    size: 1,
    cell: (info) => (
      <span className={getFlagStyles(info.getValue() ?? "")}>{flagToTitle(info.getValue() ?? "")}</span>
    ),
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      const row = props.row.original
      return (
        <span className="flex items-center justify-end gap-x-2.5">
          {row.Notes ? (
            <button onClick={() => props.setNotes(row.Notes)}>
              <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-gray-400 hover:text-gray-700" />
            </button>
          ) : null}
        </span>
      )
    },
  }),
]

export const ResultsCharts: FC<ResultsChartsProps> = ({ results }) => {
  const [search, setSearch] = useState("")

  const table = useReactTable({
    data: results.tests,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter: search,
    },
    onGlobalFilterChange: setSearch,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
  })

  const [notes, setNotes] = useState<string>()

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900">{results.name}</h3>
        </div>
        {results.tests.length > 0 ? (
          <div className="flex items-stretch gap-x-2">
            <div>
              <label htmlFor="search" className="sr-only">
                Search tests
              </label>
              <DebouncedInput
                name="search"
                type="search"
                className="input-sm mt-0"
                placeholder="Search test"
                value={search}
                onChange={(value) => setSearch(value)}
              />
            </div>
          </div>
        ) : null}
      </div>
      {results.tests.length > 0 ? (
        <div className="mt-3 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, i) => (
                        <th
                          scope="col"
                          className={classNames(
                            "relative py-2 text-left text-sm font-semibold text-gray-900",
                            i === 0
                              ? "pr-3 pl-4 sm:pl-0"
                              : i === headerGroup.headers.length - 1
                                ? "pr-4 pl-3 sm:pr-0"
                                : "px-2",
                          )}
                          key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getAllCells().map((cell, i) => (
                        <td
                          className={classNames(
                            "py-2 text-sm font-medium text-gray-500",
                            i === 0
                              ? "pr-3 pl-4 text-gray-900 sm:pl-0"
                              : i === row.getVisibleCells().length - 1
                                ? "pr-4 pl-3 sm:pr-0"
                                : "px-2",
                          )}
                          key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, {
                            ...cell.getContext(),
                            setNotes,
                          })}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-2 text-sm font-medium text-gray-500">No test code results</div>
      )}

      <Modal
        open={!!notes}
        setOpen={(open) => {
          if (!open) {
            setNotes(undefined)
          }
        }}>
        <div>
          <div className="font-medium text-gray-900">Notes</div>
          <p className="mt-3 w-[65ch] whitespace-pre-wrap text-gray-600">{formatNotes(notes ?? "")}</p>
        </div>
      </Modal>
    </div>
  )
}
